// Mock base44 client for local development
// This provides just enough structure so that the UI can work
// without a real backend.

let mockUser = null;

const DEFAULT_STARTER_CREDITS = 5;

let mockUsers = [
  {
    id: 1,
    name: 'Admin',
    email: 'admin@b-magnet.co.il',
    credits: 0,
    total_generations: 0,
    referral_code: null,
    role: 'admin',
    password: 'admin',
  },
];

let mockGenerations = [];
let mockEvents = [];
let mockCustomCharacters = [];
let mockReferrals = [];

let mockStripeIntents = {};
let mockPayPalOrders = {};

const hasWindow = typeof window !== 'undefined' && !!window.localStorage;

const STORAGE_KEYS = {
  authToken: 'base44_auth_token',
  currentUserEmail: 'base44_mock_current_user_email',
  users: 'base44_mock_users',
  generations: 'base44_mock_generations',
  events: 'base44_mock_events',
  customCharacters: 'base44_mock_custom_characters',
  referrals: 'base44_mock_referrals',
};

const getAuthToken = () => {
  if (!hasWindow) return '';
  try {
    return String(window.localStorage.getItem(STORAGE_KEYS.authToken) || '');
  } catch (e) {
    return '';
  }
};

const setAuthToken = (token) => {
  if (!hasWindow) return;
  try {
    if (!token) {
      window.localStorage.removeItem(STORAGE_KEYS.authToken);
      return;
    }
    window.localStorage.setItem(STORAGE_KEYS.authToken, String(token));
  } catch (e) {}
};

const generateReferralCode = ({ id, email }) => {
  const safeId = Number.isFinite(id) ? id : Date.now();
  const prefix = String(email || '')
    .trim()
    .slice(0, 4)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '') || 'USER';
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `REF${prefix}${String(safeId).slice(-4)}${rand}`;
};

const persistState = () => {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(STORAGE_KEYS.users, JSON.stringify(mockUsers));
    window.localStorage.setItem(STORAGE_KEYS.generations, JSON.stringify(mockGenerations));
    window.localStorage.setItem(STORAGE_KEYS.events, JSON.stringify(mockEvents));
    window.localStorage.setItem(STORAGE_KEYS.customCharacters, JSON.stringify(mockCustomCharacters));
    window.localStorage.setItem(STORAGE_KEYS.referrals, JSON.stringify(mockReferrals));
    window.localStorage.setItem(
      STORAGE_KEYS.currentUserEmail,
      mockUser?.email ? String(mockUser.email) : ''
    );
  } catch (err) {
    console.warn('[base44.mock] Failed to persist state', err);
  }
};

if (hasWindow) {
  try {
    const storedGenerations = window.localStorage.getItem(STORAGE_KEYS.generations);
    if (storedGenerations) {
      mockGenerations = JSON.parse(storedGenerations) || [];
    }
    const storedEvents = window.localStorage.getItem(STORAGE_KEYS.events);
    if (storedEvents) {
      mockEvents = JSON.parse(storedEvents) || [];
    }
    const storedCustomCharacters = window.localStorage.getItem(STORAGE_KEYS.customCharacters);
    if (storedCustomCharacters) {
      mockCustomCharacters = JSON.parse(storedCustomCharacters) || [];
    }
    const storedReferrals = window.localStorage.getItem(STORAGE_KEYS.referrals);
    if (storedReferrals) {
      mockReferrals = JSON.parse(storedReferrals) || [];
    }
    const storedUsers = window.localStorage.getItem(STORAGE_KEYS.users);
    if (storedUsers) {
      const parsedUsers = JSON.parse(storedUsers) || [];
      if (Array.isArray(parsedUsers) && parsedUsers.length > 0) {
        mockUsers = parsedUsers;
      }
    }

    mockUsers = mockUsers.map((u) => {
      if (u && !u.referral_code) {
        return { ...u, referral_code: generateReferralCode({ id: u.id, email: u.email }) };
      }
      return u;
    });

    const currentEmail = window.localStorage.getItem(STORAGE_KEYS.currentUserEmail);
    if (currentEmail) {
      const found = mockUsers.find((u) => u.email === currentEmail);
      mockUser = found ? { ...found } : null;
    }
  } catch (err) {
    console.warn('[base44.mock] Failed to load state from storage', err);
  }
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const API_BASE = '';

async function apiFetch(path, { method = 'GET', body, headers } = {}) {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  let data;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

function syncMockUserToUsers() {
  if (!mockUser) return;
  mockUsers = mockUsers.map((u) => {
    if (!u) return u;
    if (u.id === mockUser.id || String(u.email || '').toLowerCase() === String(mockUser.email || '').toLowerCase()) {
      return { ...u, ...mockUser };
    }
    return u;
  });
}

function applyMockReferralBonusIfEligible() {
  if (!mockUser?.referred_by_email || mockUser.referral_bonus_applied) return;

  const referrer = mockUsers.find(
    (u) => String(u.email || '').toLowerCase() === String(mockUser.referred_by_email).toLowerCase()
  );
  if (!referrer) return;

  const refereeBonus = 10;
  const referrerBonus = 5;

  mockUser = { ...mockUser, credits: (mockUser.credits || 0) + refereeBonus, referral_bonus_applied: true };

  mockUsers = mockUsers.map((u) => {
    if (!u) return u;
    if (u.id === referrer.id) return { ...u, credits: (u.credits || 0) + referrerBonus };
    if (u.id === mockUser.id) return { ...mockUser };
    return u;
  });

  mockReferrals = mockReferrals.map((r) => {
    if (
      String(r?.referrer_email || '').toLowerCase() === String(referrer.email || '').toLowerCase() &&
      String(r?.referee_email || '').toLowerCase() === String(mockUser.email || '').toLowerCase() &&
      !r.purchased_at
    ) {
      return {
        ...r,
        purchased_at: new Date().toISOString(),
        referrer_bonus_credited: 1,
        referee_bonus_credited: 1,
      };
    }
    return r;
  });
}

async function isServerAvailable() {
  try {
    await apiFetch('/api/health');
    return true;
  } catch (e) {
    return false;
  }
}

let serverModeValue = null;
let serverModeCheckedAt = 0;
let serverModePromise = null;
async function getServerMode() {
  // SAFETY: In production, ALWAYS enforce server mode.
  // This prevents accidental fallback to mock mode which could allow free credits.
  if (process.env.NODE_ENV === 'production') {
    return true;
  }

  const now = Date.now();
  const ttl = serverModeValue === true ? 30000 : 3000;
  if (serverModeValue !== null && now - serverModeCheckedAt < ttl) {
    return serverModeValue;
  }
  if (serverModePromise) return serverModePromise;
  serverModePromise = (async () => {
    const v = await isServerAvailable();
    serverModeValue = v;
    serverModeCheckedAt = Date.now();
    serverModePromise = null;
    return v;
  })();
  return serverModePromise;
}

const base44 = {
  auth: {
    me: async () => {
      if (await getServerMode()) {
        try {
          const { user } = await apiFetch('/api/auth/me');
          return user;
        } catch (e) {
          setAuthToken('');
          return null;
        }
      }

      // Simulate small network delay
      await delay(200);
      return mockUser ? { ...mockUser } : null;
    },

    login: async ({ email, password } = {}) => {
      if (await getServerMode()) {
        const { user, token } = await apiFetch('/api/auth/login', {
          method: 'POST',
          body: { email, password },
        });
        if (token) setAuthToken(token);
        return user;
      }

      await delay(200);
      const normalizedEmail = String(email || '').trim().toLowerCase();
      const normalizedPassword = String(password || '');

      const user = mockUsers.find((u) => (u.email || '').toLowerCase() === normalizedEmail);
      if (!user) {
        throw new Error('User not found');
      }

      if (user.password && user.password !== normalizedPassword) {
        throw new Error('Invalid credentials');
      }

      mockUser = { ...user };
      persistState();
      return { ...mockUser };
    },

    register: async ({ name, email, password, referral_code } = {}) => {
      if (await getServerMode()) {
        const { user, token } = await apiFetch('/api/auth/register', {
          method: 'POST',
          body: { name, email, password, referral_code },
        });
        if (token) setAuthToken(token);
        return user;
      }

      await delay(200);
      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error('Email is required');
      }
      if (mockUsers.some((u) => (u.email || '').toLowerCase() === normalizedEmail)) {
        throw new Error('Email already exists');
      }

      const newUser = {
        id: Date.now(),
        name: String(name || 'User').trim() || 'User',
        email: normalizedEmail,
        credits: DEFAULT_STARTER_CREDITS,
        total_generations: 0,
        referral_code: null,
        referred_by_email: null,
        referral_bonus_applied: false,
        role: 'user',
        password: String(password || ''),
      };

      newUser.referral_code = generateReferralCode(newUser);
      const safeReferralCode = String(referral_code || '').trim();
      if (safeReferralCode) {
        const referrer = mockUsers.find((u) => String(u.referral_code || '').trim() === safeReferralCode);
        if (referrer) {
          newUser.referred_by_email = referrer.email;
          mockReferrals = [
            {
              id: Date.now(),
              referrer_email: referrer.email,
              referee_email: newUser.email,
              referral_code_used: safeReferralCode,
              created_at: new Date().toISOString(),
              purchased_at: null,
              referrer_bonus_credited: 0,
              referee_bonus_credited: 0,
            },
            ...mockReferrals,
          ];
        }
      }

      mockUsers = [newUser, ...mockUsers];
      mockUser = { ...newUser };
      persistState();
      return { ...mockUser };
    },

    updateMe: async (data) => {
      if (await getServerMode()) {
        const { user } = await apiFetch('/api/auth/me', {
          method: 'PATCH',
          body: data,
        });
        return user;
      }

      if (!mockUser) {
        throw new Error('Not authenticated');
      }
      // Merge updates into mock user
      mockUser = { ...mockUser, ...data };
      mockUsers = mockUsers.map((user) =>
        user.id === mockUser.id || user.email === mockUser.email
          ? { ...user, ...data }
          : user
      );
      console.log('[base44.mock] updateMe', data);
      persistState();
      await delay(150);
      return { ...mockUser };
    },

    logout: (redirectUrl) => {
      getServerMode()
        .then((isServer) => {
          if (isServer) {
            apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
          }
        })
        .finally(() => {
          setAuthToken('');
        });

      console.log('[base44.mock] logout, redirecting to:', redirectUrl);
      mockUser = null;
      persistState();
      window.location.href = redirectUrl || '/';
    },

    redirectToLogin: () => {
      console.log('[base44.mock] redirectToLogin');
      window.location.href = '/login';
    },
  },

  admin: {
    createUser: async ({ name, email, password, role, credits } = {}) => {
      if (await getServerMode()) {
        const res = await apiFetch('/api/admin/users', {
          method: 'POST',
          body: { name, email, password, role, credits },
        });
        return res;
      }

      throw new Error('Not supported in mock mode');
    },
    listUsers: async () => {
      if (await getServerMode()) {
        const { users } = await apiFetch('/api/admin/users');
        return users;
      }

      if (!mockUser || mockUser.role !== 'admin') {
        throw new Error('Forbidden');
      }
      console.log('[base44.mock] admin.listUsers');
      await delay(150);
      return mockUsers.map((user) => ({ ...user }));
    },

    updateUser: async (userId, updates) => {
      if (await getServerMode()) {
        const { user } = await apiFetch(`/api/admin/users/${userId}`,
          {
            method: 'PATCH',
            body: updates,
          }
        );
        return user;
      }

      if (!mockUser || mockUser.role !== 'admin') {
        throw new Error('Forbidden');
      }
      mockUsers = mockUsers.map((user) =>
        user.id === userId ? { ...user, ...updates } : user
      );

      const updated = mockUsers.find((user) => user.id === userId) || null;

      if (updated && mockUser && (mockUser.id === userId || mockUser.email === updated.email)) {
        mockUser = { ...mockUser, ...updates };
      }

      console.log('[base44.mock] admin.updateUser', userId, updates);
      persistState();
      await delay(150);
      return updated ? { ...updated } : null;
    },

    getUserGenerations: async (userId, { limit } = {}) => {
      if (await getServerMode()) {
        const qs = typeof limit === 'number' ? `?limit=${encodeURIComponent(limit)}` : '';
        return await apiFetch(`/api/admin/users/${userId}/generations${qs}`);
      }
      throw new Error('Not supported in mock mode');
    },
  },

  cloudinary: {
    getSignature: async () => {
      if (await getServerMode()) {
        const data = await apiFetch('/api/cloudinary/signature');
        return data;
      }
      return {}; // Not supported in mock mode
    }
  },

  // Simple REST-like helpers (unused in most demo flows but kept for completeness)
  api: {
    get: async (url) => {
      if (await getServerMode()) {
        const data = await apiFetch(url, { method: 'GET' });
        return { data };
      }

      console.log('[base44.mock] GET', url);
      await delay(100);
      return { data: [] };
    },
    post: async (url, data) => {
      if (await getServerMode()) {
        const res = await apiFetch(url, { method: 'POST', body: data });
        return { data: res };
      }

      console.log('[base44.mock] POST', url, data);
      await delay(100);
      return { data: {} };
    },
    put: async (url, data) => {
      if (await getServerMode()) {
        const res = await apiFetch(url, { method: 'PUT', body: data });
        return { data: res };
      }

      console.log('[base44.mock] PUT', url, data);
      await delay(100);
      return { data: {} };
    },
    delete: async (url) => {
      if (await getServerMode()) {
        const res = await apiFetch(url, { method: 'DELETE' });
        return { data: res };
      }

      console.log('[base44.mock] DELETE', url);
      await delay(100);
      return { data: {} };
    },
  },

  // Mock entities used in Dashboard & Photobooth
  entities: {
    Generation: {
      create: async (payload) => {
        if (await getServerMode()) {
          const { generation } = await apiFetch('/api/generations', {
            method: 'POST',
            body: payload,
          });
          return generation;
        }

        console.log('[base44.mock] Generation.create', payload);
        await delay(200);
        if (!mockUser) {
          throw new Error('Not authenticated');
        }
        const now = new Date();
        const generation = {
          id: Date.now(),
          created_by: mockUser.email,
          created_date: now.toISOString(),
          ...payload,
        };
        mockGenerations = [generation, ...mockGenerations];
        persistState();
        return generation;
      },
      filter: async (criteria = {}, sort = '-created_date', limit = 20) => {
        if (await getServerMode()) {
          const qs = typeof limit === 'number' ? `?limit=${encodeURIComponent(limit)}` : '';
          const { generations } = await apiFetch(`/api/generations${qs}`);
          return generations;
        }

        console.log('[base44.mock] Generation.filter', criteria, sort, limit);
        await delay(150);

        let results = mockGenerations;

        // Simple equality filtering by fields in criteria (e.g. created_by)
        Object.entries(criteria || {}).forEach(([key, value]) => {
          if (value == null) return;
          results = results.filter((g) => g[key] === value);
        });

        // Sort by created_date desc if requested
        if (sort === '-created_date') {
          results = [...results].sort((a, b) =>
            (b.created_date || '').localeCompare(a.created_date || '')
          );
        }

        if (typeof limit === 'number') {
          results = results.slice(0, limit);
        }

        return results;
      },
    },
    CustomCharacter: {
      create: async (payload) => {
        console.log('[base44.mock] CustomCharacter.create', payload);
        await delay(200);
        if (!mockUser) {
          throw new Error('Not authenticated');
        }
        const now = new Date();
        const character = {
          id: Date.now(),
          created_by: mockUser.email,
          created_date: now.toISOString(),
          ...payload,
        };
        mockCustomCharacters = [character, ...mockCustomCharacters];
        persistState();
        return character;
      },

      list: async (criteria = {}) => {
        console.log('[base44.mock] CustomCharacter.list', criteria);
        await delay(150);

        let results = mockCustomCharacters;

        Object.entries(criteria || {}).forEach(([key, value]) => {
          if (value == null) return;
          results = results.filter((c) => c[key] === value);
        });

        return results;
      },
    },
    Event: {
      create: async (payload) => {
        console.log('[base44.mock] Event.create', payload);
        await delay(200);
        if (!mockUser) {
          throw new Error('Not authenticated');
        }
        const now = new Date();
        const event = {
          id: Date.now(),
          created_by: mockUser.email,
          created_date: now.toISOString(),
          ...payload,
        };
        mockEvents = [event, ...mockEvents];
        persistState();
        return event;
      },

      list: async (criteria = {}) => {
        console.log('[base44.mock] Event.list', criteria);
        await delay(150);

        let results = mockEvents;

        Object.entries(criteria || {}).forEach(([key, value]) => {
          if (value == null) return;
          results = results.filter((e) => e[key] === value);
        });

        return results;
      },
    },
    Referral: {
      filter: async (criteria) => {
        if (await getServerMode()) {
          const { referrals } = await apiFetch('/api/referrals');
          return referrals || [];
        }

        console.log('[base44.mock] Referral.filter', criteria);
        await delay(150);
        const referrerEmail = criteria?.referrer_email ? String(criteria.referrer_email).toLowerCase() : null;
        if (!referrerEmail) return [];
        return mockReferrals
          .filter((r) => String(r.referrer_email || '').toLowerCase() === referrerEmail)
          .map((r) => ({ ...r }));
      },
    },
  },

  // Mock integrations used for file upload and LLM calls
  integrations: {
    Core: {
      UploadFile: async ({ file }) => {
        console.log('[base44.mock] Core.UploadFile', file?.name || 'blob');
        await delay(300);

        // In a real backend this would return a public URL.
        // For the demo we use a local object URL so the image can be previewed.
        const fileUrl = URL.createObjectURL(file);
        return { file_url: fileUrl };
      },

      InvokeLLM: async ({ prompt }) => {
        console.log('[base44.mock] Core.InvokeLLM', prompt?.slice(0, 120));
        await delay(500);
        // The Photobooth flow only needs this call to succeed; the
        // exact structure of the response is not used.
        return {
          output: 'Mock LLM response',
        };
      },
    },

    // Payment processing functions
    payments: {
      completePurchase: async ({ credits, amount, currency = 'USD', provider = 'purchase' } = {}) => {
        const creditsToAdd = Number.isFinite(credits) ? credits : null;
        if (!Number.isInteger(creditsToAdd) || creditsToAdd <= 0) {
          throw new Error('Invalid credits');
        }

        if (await getServerMode()) {
          return apiFetch('/api/purchases/complete', {
            method: 'POST',
            body: {
              credits: creditsToAdd,
              amount: typeof amount === 'number' ? amount : undefined,
              currency,
              provider,
            },
          });
        }

        if (!mockUser) {
          throw new Error('Not authenticated');
        }

        mockUser = { ...mockUser, credits: (mockUser.credits || 0) + creditsToAdd };
        syncMockUserToUsers();
        applyMockReferralBonusIfEligible();
        syncMockUserToUsers();
        persistState();

        return {
          status: 'succeeded',
          credits_added: creditsToAdd,
          user: { ...mockUser },
        };
      },

      createStripePayment: async (amount, credits, currency = 'USD') => {
        if (await getServerMode()) {
          const res = await apiFetch('/api/payments/stripe/create', {
            method: 'POST',
            body: { amount, credits, currency },
          });
          return res;
        }

        console.log('[base44.mock] Creating Stripe payment', { amount, credits, currency });
        await delay(500);
        
        // Mock Stripe payment intent creation
        const id = 'pi_mock_' + Date.now();
        mockStripeIntents[id] = {
          id,
          credits: Number(credits) || 0,
          amount,
          currency: currency.toLowerCase(),
          status: 'requires_payment_method',
        };
        return {
          clientSecret: 'pi_mock_payment_intent_secret_' + Date.now(),
          id,
          amount,
          currency: currency.toLowerCase(),
          status: 'requires_payment_method',
          metadata: { credits }
        };
      },

      confirmStripePayment: async (paymentIntentId) => {
        if (await getServerMode()) {
          const res = await apiFetch('/api/payments/stripe/confirm', {
            method: 'POST',
            body: { paymentIntentId },
          });
          return res;
        }

        console.log('[base44.mock] Confirming Stripe payment', paymentIntentId);
        await delay(1000);
        
        // FORCE FAIL: Mock payment confirmation disabled
        throw new Error('Payment verification is disabled in mock mode.');
      },

      createPayPalOrder: async (amount, credits, currency = 'USD') => {
        if (await getServerMode()) {
            // ... (implement server call if needed, or throw)
             throw new Error('PayPal server integration not yet implemented in client');
        }
        console.log('[base44.mock] Creating PayPal order', { amount, credits, currency });
        await delay(500);

        // FORCE FAIL
        throw new Error('PayPal is disabled in mock mode.');
      },

      capturePayPalOrder: async (orderId) => {
        if (await getServerMode()) {
             throw new Error('PayPal server integration not yet implemented in client');
        }
        console.log('[base44.mock] Capturing PayPal order', orderId);
        await delay(1000);

        // FORCE FAIL
        throw new Error('PayPal capture is disabled in mock mode.');
      },

      createCryptoPayment: async (amount, credits, cryptocurrency = 'BTC') => {
        if (await getServerMode()) {
          throw new Error('Crypto payments are not available yet');
        }

        console.log('[base44.mock] Creating crypto payment', { amount, credits, cryptocurrency });
        await delay(500);
        
        // Generate mock payment address
        const address = 'mock_' + cryptocurrency.toLowerCase() + '_address_' + Date.now().toString(16);
        
        return {
          paymentAddress: address,
          amount: amount.toString(),
          cryptocurrency,
          expectedAmount: amount.toString(),
          status: 'pending',
          credits,
          qrCode: `data:image/png;base64,mock_qr_code_for_${address}`
        };
      },

      verifyCryptoPayment: async (paymentAddress) => {
        if (await getServerMode()) {
          throw new Error('Crypto payments are not available yet');
        }

        console.log('[base44.mock] Verifying crypto payment', paymentAddress);
        await delay(2000);
        // FORCE FAIL: Never return success for mock crypto
        throw new Error('Crypto verification is disabled in mock mode to prevent free credits.');
        // return { address: paymentAddress, status: 'pending' };
      },

      getPaymentHistory: async () => {
        console.log('[base44.mock] Getting payment history');
        await delay(300);
        
        // Mock payment history
        return [
          {
            id: 'payment_1',
            amount: 9.99,
            currency: 'USD',
            credits: 50,
            method: 'stripe',
            status: 'completed',
            date: new Date(Date.now() - 86400000 * 5).toISOString()
          },
          {
            id: 'payment_2',
            amount: 19.99,
            currency: 'USD',
            credits: 120,
            method: 'paypal',
            status: 'completed',
            date: new Date(Date.now() - 86400000 * 10).toISOString()
          }
        ];
      }
    }
  },
};

// Backwards compatible alias used by UI components
base44.payments = base44.integrations.payments;

export { base44 };
